<template>
    <div class="module services">
        <header class="hero">
            <p class="kicker">Tournois DartsHub</p>
            <h1 class="page-title">Services</h1>
            <p class="lead">
                DartsHub accompagne les clubs, Comités et Ligues dans l'organisation de leurs tournois de
                fléchettes : de la publication au calendrier jusqu'au lancement du tournoi dans votre logiciel,
                en passant par les inscriptions, le paiement et le pointage.
            </p>
        </header>

        <section class="section">
            <h2>Gratuit pour tout le monde</h2>
            <div class="grid grid-3">
                <article class="card-feature">
                    <i class="fa-solid fa-calendar-days"></i>
                    <h3>Calendrier des tournois</h3>
                    <p>Opens de club, compétitions de Comité, de Ligue et nationales, réunis sur un seul
                        calendrier.</p>
                </article>
                <article class="card-feature">
                    <i class="fa-solid fa-circle-info"></i>
                    <h3>Fiches événements</h3>
                    <p>Date, lieu, catégories, tarifs et infos pratiques de chaque tournoi, consultables par
                        tous.</p>
                </article>
                <article class="card-feature">
                    <i class="fa-solid fa-users"></i>
                    <h3>Liste des inscrits</h3>
                    <p>Les joueurs voient qui est déjà inscrit dans chaque catégorie.</p>
                </article>
            </div>
        </section>

        <section class="section">
            <h2>Gestion des inscriptions</h2>
            <p class="section-intro">
                Le service premium pour les organisateurs : tout ce qu'il faut pour gérer les inscriptions d'un
                tournoi, de l'ouverture du formulaire au lancement des parties.
            </p>
            <div class="grid grid-3">
                <article class="card-feature">
                    <i class="fa-solid fa-file-signature"></i>
                    <h3>Inscription en ligne</h3>
                    <p>Formulaire avec vérification de licence, tarif licencié / non licencié, catégories simple
                        et double.</p>
                </article>
                <article class="card-feature">
                    <i class="fa-solid fa-credit-card"></i>
                    <h3>Paiement HelloAsso</h3>
                    <p>Paiement en ligne via le compte HelloAsso de votre association : l'argent arrive
                        directement chez vous.</p>
                </article>
                <article class="card-feature">
                    <i class="fa-solid fa-sliders"></i>
                    <h3>Règles d'inscription</h3>
                    <p>Limites par catégorie, ouverture et fermeture des inscriptions, événements réservés aux
                        licenciés.</p>
                </article>
                <article class="card-feature">
                    <i class="fa-solid fa-clipboard-check"></i>
                    <h3>Pointage le jour J</h3>
                    <p>Catégorie par catégorie, depuis un téléphone ou un ordinateur.</p>
                </article>
                <article class="card-feature">
                    <i class="fa-solid fa-file-export"></i>
                    <h3>Export vers votre logiciel</h3>
                    <p>Darts4Windows, DartConnect, 3K Darts ou DartUP : la liste des joueurs est prête à
                        lancer.</p>
                </article>
                <article class="card-feature">
                    <i class="fa-solid fa-share-nodes"></i>
                    <h3>Lien à diffuser</h3>
                    <p>Un lien d'inscription et une fiche publique à partager à vos joueurs.</p>
                </article>
            </div>
            <div class="logos">
                <img :src="d4wLogo" alt="Darts4Windows" />
                <img :src="dartConnectLogo" alt="DartConnect" />
                <img :src="threeKLogo" alt="3K Darts" />
                <img :src="dartUpLogo" alt="DartUP" />
            </div>
            <p class="note">
                Aucune commission sur les inscriptions : HelloAsso est gratuit pour les associations et DartsHub ne
                prélève rien sur les paiements. Le tarif ci-dessous est le seul coût.
            </p>
        </section>

        <section id="tarifs" class="section pricing">
            <h2>Tarifs</h2>
            <p class="section-intro">La gestion des inscriptions se prend à l'événement ou à la saison.</p>

            <div class="sub">
                <h3>À l'unité : un seul événement</h3>
                <p class="section-intro">
                    Pour essayer l'outil ou pour un tournoi ponctuel. L'accès couvre l'événement et reste ouvert
                    30 jours après sa fin, le temps des exports et des classements.
                </p>
                <div class="grid grid-3">
                    <article class="card-price">
                        <h4>Tournoi local</h4>
                        <p class="price">15 €</p>
                        <p class="desc">Open ou soirée organisés par un club, hors calendrier officiel.</p>
                    </article>
                    <article class="card-price">
                        <h4>Tournoi officiel de comité</h4>
                        <p class="price">30 €</p>
                        <p class="desc">Open de comité, coupe du comité, championnat départemental.</p>
                    </article>
                    <article class="card-price">
                        <h4>Tournoi officiel de ligue ou national</h4>
                        <p class="price">60 €</p>
                        <p class="desc">Open de ligue, coupe de la ligue, masters, coupe de France.</p>
                    </article>
                </div>
            </div>

            <div class="sub">
                <h3>À la saison : un calendrier entier</h3>
                <p class="section-intro">
                    Du 1er août au 31 juillet, sans limite de nombre d'événements. Chaque niveau de calendrier a son
                    abonnement : le club pour ses tournois locaux, le comité pour ses compétitions officielles, la
                    ligue pour les siennes.
                </p>
                <div class="grid grid-3">
                    <article class="card-price">
                        <h4>Abonnement Club</h4>
                        <p class="price">45 €<span class="per">/ saison</span></p>
                        <p class="desc">Tous les tournois locaux du club (opens, soirées, tournois internes). Rentable
                            dès le 3e tournoi.</p>
                    </article>
                    <article class="card-price highlight">
                        <h4>Pack Comité</h4>
                        <p class="price">100 €<span class="per">/ saison</span></p>
                        <p class="desc">Tous les officiels de niveau Comité (Opens, Coupe, Interclubs) du Comité, quel
                            que soit le club hôte qui gère et encaisse.</p>
                    </article>
                    <article class="card-price">
                        <h4>Pack Ligue</h4>
                        <p class="price">250 €<span class="per">/ saison</span></p>
                        <p class="desc">Tous les officiels de niveau Ligue (Opens, Coupe, Masters) de la Ligue,
                            qu'elle les gère elle-même ou les délègue à un club hôte.</p>
                    </article>
                </div>
                <p class="note">
                    Abonnement pris après le 1er février : moitié prix pour le reste de la saison.
                </p>
            </div>

            <div class="sub rule">
                <h3>Qui paie quoi ?</h3>
                <p>
                    Les compétitions officielles sont hébergées par des clubs, qui gèrent les inscriptions et
                    encaissent sur leur propre compte HelloAsso. Mais c'est le <strong>propriétaire du
                        calendrier</strong> qui s'abonne : le comité pour ses officiels, la ligue pour les siens. Le
                    club
                    hôte n'a rien à payer.
                </p>
                <ul class="examples">
                    <li>Un club héberge l'Open de son comité et le comité a le pack : le club gère et encaisse, rien à
                        payer.</li>
                    <li>Un club héberge l'Open de son comité et le comité n'a pas le pack : accès à l'unité, 30 €,
                        payé par le club ou par le comité.</li>
                    <li>Un club organise trois opens locaux dans la saison : abonnement club à 45 €, ou 15 € par
                        open.</li>
                    <li>Le pack ligue couvre les opens de ligue, la coupe de la ligue et les masters, pas les opens de
                        comité. Le pack comité ne couvre pas les tournois locaux des clubs. Les compétitions
                        fédérales (Coupe de France...) se prennent à l'unité.</li>
                </ul>
            </div>

            <div class="sub">
                <h3>Comment ça se passe</h3>
                <ol class="steps">
                    <li>Vous nous écrivez en précisant l'événement ou l'abonnement souhaité.</li>
                    <li>Nous activons l'accès et vous envoyons la facture ; le règlement se fait par virement.</li>
                    <li>Vous renseignez vos identifiants HelloAsso dans votre espace, et vous ouvrez les
                        inscriptions.</li>
                </ol>
                <p class="note">
                    Envie de tester avant ? Nous pouvons créer un événement de démonstration, sans paiement, pour que
                    votre bureau essaie le formulaire et le pointage.
                </p>
                <div class="cta-row">
                    <a :href="`${authUrl}/contact`" target="_blank" rel="noopener noreferrer" class="btn-cta">
                        Nous contacter
                    </a>
                    <router-link to="/" class="cta-link">Voir le calendrier</router-link>
                </div>
            </div>
        </section>
    </div>
</template>

<script setup lang="ts">
import { auth_url as authUrl } from '@/config/config'
import d4wLogo from '@/assets/darts4windows-logo.jpg'
import dartConnectLogo from '@/assets/dartconnect-logo.png'
import threeKLogo from '@/assets/3kdarts-logo.png'
import dartUpLogo from '@/assets/dartup-logo.png'
</script>

<style scoped>
.services {
    max-width: 960px;
    margin-left: auto;
    margin-right: auto;
}

.hero {
    text-align: center;
    margin-bottom: 2rem;
}

.kicker {
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--color-accent);
    margin: 0 0 0.25rem;
}

.lead {
    max-width: 68ch;
    margin: 0.5rem auto 0;
    color: var(--color-fg-darker);
    font-size: 1rem;
    line-height: 1.55;
}

.section {
    margin-top: 2.25rem;
}

.section h2 {
    font-size: 1.4rem;
    margin: 0 0 0.6rem;
}

.section h3 {
    font-size: 1.15rem;
    margin: 0 0 0.6rem;
}

.section-intro {
    color: var(--color-fg-darker);
    margin: 0 0 1rem;
    line-height: 1.5;
}

.sub {
    margin-top: 1.75rem;
}

.pricing {
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 1px solid var(--color-light-shadow);
    scroll-margin-top: 140px;
}

.pricing>h2 {
    text-align: center;
    font-size: 1.6rem;
}

.pricing>.section-intro {
    text-align: center;
}

.examples {
    margin: 0;
    padding-left: 1.2rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    line-height: 1.45;
}

.note {
    margin: 1rem 0 0;
    padding: 0.75rem 1rem;
    border-left: 3px solid var(--color-accent);
    background: color-mix(in srgb, var(--color-accent) 10%, transparent);
    border-radius: 0 var(--radius, 8px) var(--radius, 8px) 0;
    font-size: 0.92rem;
    line-height: 1.5;
}

.grid {
    display: grid;
    gap: 1rem;
}

.grid-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.card-feature {
    background: var(--color-bg-lighter);
    border: 1px solid var(--color-light-shadow);
    border-radius: var(--radius, 10px);
    padding: 1.1rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.4rem;
}

.card-feature i {
    font-size: 1.3rem;
    color: var(--color-accent);
}

.card-feature h3 {
    margin: 0;
    font-size: 1rem;
}

.card-feature p {
    margin: 0;
    font-size: 0.9rem;
    color: var(--color-fg-darker);
    line-height: 1.45;
}

.logos {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: center;
    justify-items: center;
    gap: 1rem;
    margin-top: 1rem;
    padding: 1rem 1.25rem;
    background: var(--color-bg-lighter);
    border: 1px solid var(--color-light-shadow);
    border-radius: var(--radius, 10px);
    box-sizing: border-box;
}

.logos img {
    height: 40px;
    max-width: 100%;
    object-fit: contain;
    border-radius: 6px;
}

.card-price {
    background: var(--color-bg-lighter);
    border: 1px solid var(--color-light-shadow);
    border-radius: var(--radius, 10px);
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.card-price.highlight {
    border-color: var(--color-accent);
    box-shadow: 0 0 0 1px var(--color-accent);
}

.card-price h4 {
    margin: 0;
    font-size: 1rem;
}

.price {
    margin: 0;
    font-size: 2rem;
    font-weight: 800;
    color: var(--color-accent);
    line-height: 1.1;
}

.per {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--color-fg-darker);
    margin-left: 0.35rem;
}

.desc {
    margin: 0;
    font-size: 0.9rem;
    color: var(--color-fg-darker);
    line-height: 1.45;
}

.rule {
    padding: 1.25rem;
    border: 1px dashed var(--color-light-shadow);
    border-radius: var(--radius, 10px);
}

.rule p {
    margin: 0 0 0.75rem;
    line-height: 1.5;
}

.steps {
    margin: 0;
    padding-left: 1.3rem;
    display: flex;
    flex-direction: column;
    gap: 0.45rem;
    line-height: 1.45;
}

.cta-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 1.25rem;
}

.btn-cta {
    display: inline-flex;
    align-items: center;
    padding: 0.65rem 1.4rem;
    border-radius: var(--radius, 8px);
    background: var(--color-accent);
    color: var(--color-bg);
    font-weight: 700;
    text-decoration: none;
}

.btn-cta:hover {
    filter: brightness(1.08);
}

@media (max-width: 720px) {
    .grid-3 {
        grid-template-columns: 1fr;
    }

    .price {
        font-size: 1.7rem;
    }

    .logos {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
